import React, { useEffect, useRef, useState } from 'react';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import ContactSalesMenu from './ContactSalesMenu';
import {
  VerificationPageState,
  VerificationSummary,
} from './verificationReportSummary';

interface EntityVerificationBandProps {
  /** The verification page's own state for this record. */
  state: VerificationPageState;
  /** The verification page's own figure, tail and sentence. */
  summary: VerificationSummary;
  entityName: string;
  startedAt?: string | null;
  runDisabled?: boolean;
  /**
   * Why a run cannot start (no credits left, the database being screened), from
   * verificationRunBlockedReason; disables the run and becomes its tooltip.
   */
  runBlockedReason?: string | null;
  /**
   * The team's Business Verification credits: `remaining` this month out of a
   * monthly `total`, shown when loaded. Null until loaded or when it can't be
   * read.
   */
  credits?: { total: number; remaining: number } | null;
  /** The database has verification turned off; hides the run action. */
  blockedReason?: 'database-off' | null;
  onRequestRun: () => void;
  onFullReport: () => void;
}

// The strip's tone classes: red for a failed check or an interrupted run,
// amber for checks to review or nothing to read, blue while running.
const STATE_CLASS: Partial<Record<VerificationPageState, string>> = {
  running: 'state-running',
  failed: 'state-interrupted',
  error: 'state-interrupted',
  open: 'state-open',
  noinput: 'state-open',
  never: 'state-not-run',
  dboff: 'state-not-run',
};

/**
 * The record page's summary of business verification. It reads the same
 * state and wording as the verification page, so the two never disagree; a
 * record whose checks all passed shows no band (the record head carries a
 * Verified pill instead). A Team without the Entity add-on, or one the
 * service refuses because its add-on flag is stale, is pointed to sales and
 * the report itself stays out of reach.
 */
const EntityVerificationBand: React.FC<EntityVerificationBandProps> = ({
  state,
  summary,
  entityName,
  startedAt,
  runDisabled = false,
  runBlockedReason = null,
  credits = null,
  blockedReason = null,
  onRequestRun,
  onFullReport,
}) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (state !== 'running') return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [state]);

  // Screen readers hear a run start and finish, not every elapsed second.
  const announcedStateRef = useRef(state);
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    const previous = announcedStateRef.current;
    announcedStateRef.current = state;
    if (previous === state) return;
    if (state === 'running') {
      setAnnouncement(`Business verification running for ${entityName}.`);
    } else if (previous === 'running') {
      setAnnouncement(`Business verification finished for ${entityName}.`);
    }
  }, [entityName, state]);

  const liveRegion = (
    <span className='bv-sr-only' role='status' aria-live='polite'>
      {announcement}
    </span>
  );
  if (state === 'clean') return liveRegion;

  const startedAtTime = startedAt ? new Date(startedAt).getTime() : NaN;
  const elapsedSeconds = Number.isFinite(startedAtTime)
    ? Math.max(0, Math.floor((now - startedAtTime) / 1000))
    : null;

  const detail =
    state === 'running'
      ? elapsedSeconds === null
        ? 'running'
        : `${elapsedSeconds}s elapsed`
      : summary.tail;
  const message =
    blockedReason && state !== 'running' && state !== 'noplan'
      ? "Business verification is turned off for this database. A team admin can turn it on in the database's screening settings."
      : summary.summary;

  const runLabel =
    state === 'error'
      ? 'Try again'
      : state === 'never'
      ? 'Run verification'
      : 'Run again';
  // A record with nothing to read cannot run; the full report says what to add.
  const canRun =
    !blockedReason &&
    state !== 'running' &&
    state !== 'noinput' &&
    state !== 'noplan';

  return (
    <section
      className={`entity-hub-verification-strip ${STATE_CLASS[state] || ''}`}
    >
      {liveRegion}
      <VerifiedUserOutlinedIcon className='entity-hub-verification-icon' />
      <div className='entity-hub-verification-count'>
        <span>Business verification</span>
        <strong>{summary.figure}</strong>
        <small>{detail}</small>
        {credits && credits.total > 0 ? (
          <small className='entity-hub-verification-credits'>
            Remaining credits this month: {credits.remaining.toLocaleString()}
          </small>
        ) : null}
      </div>

      <div className='entity-hub-verification-message'>
        {state === 'running' ? (
          <span className='entity-hub-verification-sweep' aria-hidden='true' />
        ) : null}
        <p title={message}>{message}</p>
      </div>

      <div className='entity-hub-verification-actions'>
        {state === 'noplan' ? (
          <ContactSalesMenu className='entity-hub-btn compact primary' />
        ) : null}
        {canRun ? (
          <button
            type='button'
            className={`entity-hub-btn compact${
              state === 'never' ? ' primary' : ''
            }`}
            onClick={onRequestRun}
            disabled={runDisabled || Boolean(runBlockedReason)}
            title={runBlockedReason || undefined}
          >
            {runLabel}
          </button>
        ) : null}
      </div>

      {state === 'noplan' ? null : (
        <button
          type='button'
          className='entity-hub-text-btn'
          onClick={onFullReport}
        >
          {state === 'never' || state === 'noinput'
            ? 'What it checks'
            : 'Full report'}
          <KeyboardArrowRightOutlinedIcon />
        </button>
      )}
    </section>
  );
};

export default EntityVerificationBand;
